import { useCallback, useEffect, useMemo, useRef } from "react";
import { clearFolderSnapshot } from "@/lib/mail/inboxFolderBus";
import { refreshFolderFromImap, syncFolderFromServerCache, syncFolderHeaders } from "@/lib/mail/folderWarmup";
import {
  finishMailMutation,
  setMailMutationPhase,
} from "@/lib/mail/mailMutationProgress";
import { hasMutationHold } from "@/lib/mail/mailMutationHold";
import { createRefreshScheduler } from "@/lib/mail/mailRefreshSchedule";

type InboxRefreshApi = {
  reload: (
    refresh?: boolean,
    options?: { background?: boolean; silent?: boolean }
  ) => Promise<void>;
  refreshInBackground: () => void;
  pullInboxNow: () => Promise<void>;
};

type FoldersRefreshApi = {
  reload: (refresh?: boolean) => Promise<void | boolean>;
  refreshBadges?: () => Promise<void>;
  adjustFolderUnread: (folder: string, delta: number) => void;
};

export type MailFolderInvalidation = {
  accountId: number;
  folders: string[];
  /** Resynchroniser les en-têtes IMAP avant reload liste */
  syncHeaders?: boolean;
  /** Recharger la liste si un dossier affiché est invalidé */
  reloadListIfViewing?: boolean;
  forceList?: boolean;
  /** Conserver le snapshot bus (ex. source déjà patchée optimiste après move) */
  preserveSnapshots?: string[];
  /** Prefetch DD02 en arrière-plan (sans reload liste) */
  prefetchFolders?: string[];
  /** Relit le dossier sur IMAP (après un déplacement, le cache n’a pas encore le nouvel UID). */
  prefetchRefresh?: boolean;
  /** Pastille mutation : phase IMAP après sync serveur */
  mutationJobId?: string;
  /** Rafraîchir la liste complète des dossiers (IMAP lourd) — défaut false */
  forceFolderReload?: boolean;
};

export type MailInboxListRefresh = {
  force?: boolean;
  silent?: boolean;
  background?: boolean;
};

/**
 * Point d’entrée unique pour invalider listes + badges dossiers après une mutation
 * (brouillon, delete, move, envoi, etc.).
 */
export function useMailRefresh(
  accountId: number | undefined,
  viewingFolder: string,
  inbox: InboxRefreshApi,
  folders: FoldersRefreshApi
) {
  const schedulerRef = useRef(createRefreshScheduler(280));
  const ctxRef = useRef({ accountId, viewingFolder, inbox, folders });
  ctxRef.current = { accountId, viewingFolder, inbox, folders };

  useEffect(() => {
    const scheduler = schedulerRef.current;
    return () => scheduler.cancelAll();
  }, []);

  const refreshFolders = useCallback((force = true) => {
    const id = ctxRef.current.accountId;
    schedulerRef.current.schedule(`folders:${id ?? 0}`, () => {
      void ctxRef.current.folders.reload(force);
    });
  }, []);

  const refreshInboxList = useCallback((options: MailInboxListRefresh = {}) => {
    const { force = false, silent = true, background = true } = options;
    const { accountId: id, viewingFolder: folder } = ctxRef.current;
    schedulerRef.current.schedule(`inbox:${id ?? 0}:${folder}`, () => {
      void ctxRef.current.inbox.reload(force, { background, silent });
    });
  }, []);

  const syncInBackground = useCallback(() => {
    const { accountId: id, viewingFolder: folder } = ctxRef.current;
    schedulerRef.current.schedule(`bg:${id ?? 0}:${folder}`, () => {
      ctxRef.current.inbox.refreshInBackground();
    });
  }, []);

  /** Sync liste courante + badges dossiers (nouveau mail, warm, envoi, etc.) */
  const refreshMailboxQuiet = useCallback(() => {
    syncInBackground();
    if (ctxRef.current.folders.refreshBadges) {
      void ctxRef.current.folders.refreshBadges();
    } else {
      refreshFolders(false);
    }
  }, [refreshFolders, syncInBackground]);

  /** Après échec API : resync sans vider la liste optimiste */
  const recoverAfterMutationError = useCallback(() => {
    refreshMailboxQuiet();
  }, [refreshMailboxQuiet]);

  const invalidateFolders = useCallback((spec: MailFolderInvalidation) => {
    const preserve = new Set(spec.preserveSnapshots ?? []);

    // Ne plus vider le snapshot systématiquement : les pastilles retombaient sur
    // STATUS IMAP périmé (fantôme unifié / comptes) alors que la liste était à jour
    // via patchSeen / patchRemove. Clear seulement si reload dossiers forcé.
    if (spec.forceFolderReload) {
      for (const folder of spec.folders) {
        if (preserve.has(folder)) continue;
        clearFolderSnapshot(spec.accountId, folder);
      }
    }

    if (spec.forceFolderReload) {
      refreshFolders(true);
    } else if (ctxRef.current.folders.refreshBadges) {
      void ctxRef.current.folders.refreshBadges();
    } else {
      refreshFolders(false);
    }

    const prefetch = spec.prefetchFolders ?? [];
    for (const folder of prefetch) {
      if (preserve.has(folder)) continue;
      schedulerRef.current.schedule(`prefetch:${spec.accountId}:${folder}`, () => {
        if (spec.prefetchRefresh) {
          void refreshFolderFromImap(spec.accountId, folder);
          return;
        }
        void syncFolderFromServerCache(spec.accountId, folder);
      });
    }

    const { accountId: currentAccount, viewingFolder: currentFolder } = ctxRef.current;
    const shouldReloadList =
      spec.reloadListIfViewing === true &&
      spec.accountId === currentAccount &&
      spec.folders.includes(currentFolder) &&
      !hasMutationHold(spec.accountId, currentFolder);

    const finishMutation = () => {
      if (spec.mutationJobId) {
        finishMailMutation(spec.mutationJobId);
      }
    };

    if (!shouldReloadList) {
      if (spec.mutationJobId) {
        setMailMutationPhase(spec.mutationJobId, "imap-sync");
        schedulerRef.current.schedule(`mut-bg:${spec.mutationJobId}`, () => {
          finishMutation();
        });
      }
      return;
    }

    schedulerRef.current.schedule(
      `invalidate:${spec.accountId}:${currentFolder}`,
      () => {
        void (async () => {
          if (spec.mutationJobId) {
            setMailMutationPhase(spec.mutationJobId, "imap-sync");
          }
          if (spec.syncHeaders) {
            await syncFolderHeaders(spec.accountId, currentFolder);
          }
          await ctxRef.current.inbox.reload(spec.forceList ?? false, {
            background: true,
            silent: true,
          });
          finishMutation();
        })();
      }
    );
  }, [refreshFolders]);

  /** Bouton « Actualiser » utilisateur */
  const pullMailbox = useCallback(async () => {
    schedulerRef.current.cancelAll();
    await ctxRef.current.inbox.pullInboxNow();
    if (ctxRef.current.folders.refreshBadges) {
      await ctxRef.current.folders.refreshBadges();
    } else {
      await ctxRef.current.folders.reload(false);
    }
  }, []);

  const adjustFolderUnread = useCallback(
    (folder: string, delta: number) => {
      ctxRef.current.folders.adjustFolderUnread(folder, delta);
    },
    []
  );

  return useMemo(
    () => ({
      refreshFolders,
      refreshInboxList,
      syncInBackground,
      refreshMailboxQuiet,
      recoverAfterMutationError,
      invalidateFolders,
      pullMailbox,
      adjustFolderUnread,
    }),
    [
      adjustFolderUnread,
      invalidateFolders,
      pullMailbox,
      recoverAfterMutationError,
      refreshFolders,
      refreshInboxList,
      refreshMailboxQuiet,
      syncInBackground,
    ]
  );
}
